import { callAct, isOk } from './apiConfig';

/*
 * businessConfig — the business's operational settings (הגדרות עסק), backed by the
 * businessConfig table + getBusinessConfig / updateBusinessConfig acts. Scoped server-side
 * to the session business (areaID 0 = business-wide). Powers the SeatManagement settings
 * screen AND the LIVE board's timing (seating duration + the "upcoming reservation" lead).
 *
 * Field meanings the app uses:
 *   maxTimeTableIsInUseInMinutes          — default dining duration (how long a party holds a table)
 *   upcomingReservationLeadMinutes        — how many minutes before a booked reservation its table
 *                                           is flagged "upcoming/saved" on the board (the X time)
 *   tableNotAviableWhileOrderingInMinutes — mark a table taken from the moment ordering starts
 *   saveTableForLateCustomerInMinutes     — how long to hold a booked table for a late customer
 *   maxPercentForOrdersBeforeFull         — max occupancy % before the business is "full"
 */

// Safe defaults mirror the board's historical hardcoded values, so nothing breaks if the act
// isn't reachable.
export const CONFIG_DEFAULTS = {
    maxTimeTableIsInUseInMinutes: 90,
    upcomingReservationLeadMinutes: 30,
    tableNotAviableWhileOrderingInMinutes: 15,
    saveTableForLateCustomerInMinutes: 10,
    maxPercentForOrdersBeforeFull: 100,
    timeGapInMinutesForOrder: 15,
    autoSmsToWaitingListTimeInMinutes: 10,
    maxPeopleInUnitedTable: 0,
    bookingCutoffMinutesBeforeClose: 0,
    phoneBookingThresholdGuests: 0,
};

const toNum = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
};

// Normalize a raw config row → the numeric fields the app reads (with defaults).
function normalize(row) {
    const out = { ...CONFIG_DEFAULTS };
    if (row && typeof row === 'object') {
        Object.keys(CONFIG_DEFAULTS).forEach((k) => {
            if (row[k] != null) out[k] = toNum(row[k], CONFIG_DEFAULTS[k]);
        });
    }
    return out;
}

// Load the business's config. Returns the normalized settings (defaults on error — never throws).
export async function getConfig() {
    try {
        const envelope = await callAct('getBusinessConfig', {});
        if (isOk(envelope) && envelope.data) return normalize(envelope.data);
    } catch (e) { /* act not available */ }
    return { ...CONFIG_DEFAULTS };
}

// Save a PARTIAL set of settings (only send the fields you changed). Returns { ok, config }.
// config is the fresh server row (clamped) on success.
export async function saveConfig(partial) {
    try {
        // Only forward known numeric fields, as integers (the server also clamps).
        const payload = {};
        Object.keys(CONFIG_DEFAULTS).forEach((k) => {
            if (partial && partial[k] != null && partial[k] !== '') payload[k] = Math.round(Number(partial[k]));
        });
        if (Object.keys(payload).length === 0) return { ok: false };
        const envelope = await callAct('updateBusinessConfig', payload);
        return { ok: isOk(envelope), config: isOk(envelope) ? normalize(envelope.data) : null };
    } catch (e) { return { ok: false }; }
}

// The EFFECTIVE hold window (minutes) the engine uses for availability: seating duration +
// the "save reserved table" grace. Mirrors the server's effHoldMinutes() so the client's
// free-table lists match the server's checks.
export function effectiveHold(cfg) {
    const dur = Number(cfg?.maxTimeTableIsInUseInMinutes) || CONFIG_DEFAULTS.maxTimeTableIsInUseInMinutes;
    const grace = Math.max(0, Number(cfg?.saveTableForLateCustomerInMinutes) || 0);
    return dur + grace;
}

export default { getConfig, saveConfig, effectiveHold, CONFIG_DEFAULTS };
